/**
 * KPI figures from numbers — the prototype's `buildKpis`, minus the rows.
 *
 * A contract `Kpi` carries the current figure and, when a comparison period is
 * applied, the previous one. `previous: null` means "no comparison" and draws
 * no badge; a previous of 0 draws no percent badge either (no honest % change
 * from zero), exactly as before.
 */
import type { ChartTooltipComparison, KPIMetric } from '@deepdive-ui';

import { metricMeta, type MetricId } from './metrics';

export type Kpi = { current: number; previous: number | null };

const pct = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
const pts = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(1)} pts`;

export function delta(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

export function toneFor(metricId: MetricId, change: number): KPIMetric['tone'] {
  if (change === 0) return undefined;
  const improved = metricMeta[metricId].goodDirection === 'up' ? change > 0 : change < 0;
  return improved ? 'positive' : 'negative';
}

export function kpiMetric(metricId: MetricId, kpi: Kpi, label?: string): KPIMetric {
  const meta = metricMeta[metricId];
  const name = label ?? meta.label;
  const value = meta.format(kpi.current);
  if (kpi.previous === null) return { label: name, value };

  if (meta.deltaMode === 'points') {
    const gap = kpi.current - kpi.previous;
    return {
      label: name,
      value,
      tone: gap === 0 ? undefined : toneFor(metricId, gap),
      badge: pts(gap),
    };
  }
  const change = delta(kpi.current, kpi.previous);
  if (change === null) return { label: name, value };
  return { label: name, value, tone: toneFor(metricId, change), badge: pct(change) };
}

/**
 * One series' comparison reading in a chart tooltip — the same badge rules as
 * `kpiMetric`: points for a `deltaMode: 'points'` metric, a percentage
 * otherwise, and no percentage off a previous of 0. Toned by `toneOfChange`.
 */
export function comparisonReading(
  metricId: MetricId,
  current: number,
  previous: number,
): ChartTooltipComparison {
  const meta = metricMeta[metricId];
  const reading = { previous: meta.format(previous) };
  const raw = meta.deltaMode === 'points' ? current - previous : delta(current, previous);
  if (raw === null) return reading;
  const change = shownChange(raw);
  return {
    ...reading,
    change: meta.deltaMode === 'points' ? pts(change) : pct(change),
    tone: toneOfChange(change),
  };
}

/**
 * A change as its badge prints it, to one decimal. A tooltip badge is toned off
 * this rather than the raw change, so a move too small to show reads a grey
 * "+0.0", never a coloured one; `|| 0` folds a rounded −0 into 0.
 */
export function shownChange(change: number): number {
  return Math.round(change * 10) / 10 || 0;
}

/**
 * A tooltip badge's colour: up is green, down is red, unchanged is grey —
 * whatever the metric. The badge reports the move; the reader judges it.
 */
export function toneOfChange(change: number): NonNullable<ChartTooltipComparison['tone']> {
  return change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral';
}
